import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface ProductItem {
  id: number;
  name: string;
  description?: string;
}

interface UserOption {
  id: string;
  nome: string;
  email: string;
}

export default function ReleaseManager() {
  const { user } = useAuth();
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedReleaseProduct, setSelectedReleaseProduct] = useState<number | "">("");
  const [liberando, setLiberando] = useState(false);
  const [liberacaoMsg, setLiberacaoMsg] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [{ data: prodData }, { data: usersData }] = await Promise.all([
      supabase.from("product_items").select("id, name, description").order("name", { ascending: true }).limit(2000),
      supabase.from("profiles").select("id, nome, email").order("nome").limit(2000),
    ]);
    if (prodData) setProducts(prodData as ProductItem[]);
    if (usersData) setUsers(usersData as UserOption[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleLiberarManual(e: React.FormEvent) {
    e.preventDefault();
    setLiberacaoMsg("");
    if (!selectedUser || !selectedReleaseProduct) {
      setLiberacaoMsg("Selecione um usuário e uma partitura.");
      return;
    }
    setLiberando(true);
    try {
      const { data: asset } = await supabase
        .from("digital_assets")
        .select("pdf_url")
        .eq("product_id", selectedReleaseProduct)
        .maybeSingle();

      const { error } = await supabase.from("user_library").insert({
        user_id: selectedUser,
        product_id: Number(selectedReleaseProduct),
        order_id: null,
        purchased_at: new Date().toISOString(),
        pdf_url: asset?.pdf_url || null,
        metadata: { liberado_manualmente: true, admin_id: user?.id },
      });

      if (error) throw new Error(error.message);
      setLiberacaoMsg("Partitura liberada com sucesso para o usuário!");
      setSelectedUser("");
      setSelectedReleaseProduct("");
    } catch (err: any) {
      setLiberacaoMsg(err.message || "Erro ao liberar partitura.");
    } finally {
      setLiberando(false);
    }
  }

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.description || "").toLowerCase().includes(productSearch.toLowerCase())
  );

  const filteredUsers = users.filter((u) =>
    (u.nome || "").toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div>
      <p className="text-godoy-lightgray text-sm mb-4">
        Libere uma partitura diretamente na biblioteca de um usuário, sem necessidade de pagamento. Útil para testes e brindes.
      </p>
      {liberacaoMsg && (
        <div className={`mb-4 p-3 rounded-md text-sm ${liberacaoMsg.includes("sucesso") ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400" : "bg-red-500/10 border border-red-500/30 text-red-400"}`}>
          {liberacaoMsg}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      <form onSubmit={handleLiberarManual} className="space-y-5">
        {/* Usuário com busca */}
        <div>
          <label className="block text-sm text-godoy-lightgray mb-1.5">Usuário</label>
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 mb-2"
          />
          <select
            required
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream focus:outline-none focus:border-godoy-gold/50 text-sm appearance-none"
            size={Math.min(6, Math.max(3, filteredUsers.length))}
          >
            <option value="">Selecione um usuário...</option>
            {filteredUsers.slice(0, 200).map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome || "Sem nome"} — {u.email}
              </option>
            ))}
          </select>
          {filteredUsers.length > 200 && (
            <p className="text-xs text-godoy-lightgray/50 mt-1">
              Mostrando 200 de {filteredUsers.length} usuários. Use a busca para filtrar.
            </p>
          )}
        </div>

        {/* Partitura com busca */}
        <div>
          <label className="block text-sm text-godoy-lightgray mb-1.5">Partitura / Produto</label>
          <input
            type="text"
            placeholder="Buscar por nome ou descrição..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 mb-2"
          />
          <select
            required
            value={selectedReleaseProduct}
            onChange={(e) => setSelectedReleaseProduct(e.target.value ? Number(e.target.value) : "")}
            className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream focus:outline-none focus:border-godoy-gold/50 text-sm appearance-none"
            size={Math.min(10, Math.max(3, filteredProducts.length))}
          >
            <option value="">Selecione uma partitura...</option>
            {filteredProducts.slice(0, 500).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} (ID: {p.id})
              </option>
            ))}
          </select>
          {filteredProducts.length > 500 && (
            <p className="text-xs text-godoy-lightgray/50 mt-1">
              Mostrando 500 de {filteredProducts.length} partituras. Use a busca para filtrar.
            </p>
          )}
        </div>

        <div>
          <button
            type="submit"
            disabled={liberando}
            className="w-full py-2.5 bg-godoy-gold/80 text-godoy-black font-semibold rounded-md hover:bg-godoy-gold transition-all text-sm disabled:opacity-60 whitespace-nowrap"
          >
            {liberando ? "Liberando..." : "Liberar Partitura"}
          </button>
        </div>
      </form>
    </div>
  );
}